import React, { useMemo, useState } from 'react';

import moment from 'moment';
import * as Yup from 'yup';

import { Report } from '~/components';
import { Autocomplete, DateTime, RadioGroup } from '~/components/Form';
import { t } from '~/helpers';

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm:ss';

export default function RelMedicao() {
  const [open, setOpen] = useState(true);
  const [posto, setPosto] = useState('');

  const initials = useMemo(
    () => ({
      datahorainicial: moment()
        .subtract(7, 'days')
        .startOf('day')
        .format(DATE_TIME_FORMAT),
      datahorafinal: moment().endOf('day').format(DATE_TIME_FORMAT),
      formato: 'pdf',
    }),
    []
  );

  async function handleValidate(params) {
    try {
      const requiredMessage = t('s.geral.validacao.campoobrigatorio');
      const dateSchema = Yup.string()
        .nullable()
        .required(requiredMessage)
        .test(
          'data-valida',
          t('s.geral.validacao.campoinvalido'),
          (value) => !value || moment(value, DATE_TIME_FORMAT, true).isValid()
        );

      const schema = Yup.object().shape({
        idposto: Yup.string().nullable().required(requiredMessage),
        idtanquefisico: Yup.string().nullable().required(requiredMessage),
        datahorainicial: dateSchema,
        datahorafinal: dateSchema.test(
          'periodo-valido',
          t('s.geral.validacao.data_final_maior_inicial'),
          function validatePeriod(value) {
            const start = moment(
              this.parent.datahorainicial,
              DATE_TIME_FORMAT,
              true
            );
            const end = moment(value, DATE_TIME_FORMAT, true);

            if (!start.isValid() || !end.isValid()) return true;
            if (end.isBefore(start)) return false;

            if (end.isAfter(start.clone().add(31, 'days'))) {
              return this.createError({
                message: t('s.relatorio.medicao.periodomaximo'),
              });
            }

            return true;
          }
        ),
      });

      await schema.validate(params, { abortEarly: false });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const form = {};

        err.inner.forEach((error) => {
          form[error.path] = error.message;
        });

        // eslint-disable-next-line no-throw-literal
        throw { form };
      }

      throw err;
    }
  }

  return (
    <Report
      open={open}
      onClose={() => setOpen(false)}
      title={t('s.menu.relatorio.relmedicao')}
      relatorio="relmedicao"
      validate={handleValidate}
      initials={initials}
    >
      <Autocomplete
        name="idposto"
        label="codigonomeposto"
        url="postos"
        unique="idposto"
        title={t('s.menu.configuracao.posto')}
        hiddens={[{ name: 'codigonomeposto' }]}
        params={{ filteruser: true }}
        getValue={(value) => setPosto(value.idposto ?? '')}
      />

      <DateTime
        name="datahorainicial"
        label={t('s.geral.opcoes.datahorainicial')}
        serialize={(value) =>
          value.set({ seconds: 0 }).format(DATE_TIME_FORMAT)
        }
        time
      />

      <DateTime
        name="datahorafinal"
        label={t('s.geral.opcoes.datahorafinal')}
        serialize={(value) =>
          value.set({ seconds: 59 }).format(DATE_TIME_FORMAT)
        }
        time
      />

      <Autocomplete
        key={`tanquefisico-${posto}`}
        name="idtanquefisico"
        label="codigotanquefisico"
        url="tanquesfisicos"
        unique="idtanquefisico"
        title={t('s.menu.configuracao.tanquefisico')}
        hiddens={[{ name: 'codigotanquefisico' }]}
        disabled={!posto}
        params={{
          wherefield: ['idposto'],
          valuewherefield: [posto || 0],
          typewherefield: ['EQUALS'],
          filteruser: true,
        }}
      />

      <RadioGroup
        name="formato"
        label={t('s.relatorio.geral.formato')}
        options={[
          { label: 'PDF', value: 'pdf' },
          { label: 'Excel', value: 'excel' },
        ]}
        defaultValue="pdf"
        serialize={(value) => value}
        row
      />
    </Report>
  );
}
